'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Live interactive takeoff explorer for the Takeoff Forecast, framed from
// plan-a.aifuturesmodel.com. `?embed=1` makes that page render only the
// explorer widget (no site header, no supplement text). Rendered above the
// supplement's title via EssayPage's `aboveTitle` slot (see
// app/supplements/takeoff-forecast/page.tsx).
//
// The widget is wide and scrolls internally (horizontally; vertically the
// frame grows to fit — see frameHeight), so the frame breaks
// out of the essay column to the full viewport width. The classic CSS-only
// breakout (relative left-1/2 w-screen -ml-[50vw], as on the econ-explorer
// page) centers the plate on its *parent's* center — and the essay column
// (grid-area: content in .main-grid) is not viewport-centered: the toc rail
// and the 430px graph column flank it asymmetrically, so that trick would
// leave a wide gap at the right viewport edge and clip off-screen on the
// left. Instead we measure where the block statically sits and pull it left
// by exactly that much, sizing it to the viewport's visible width
// (clientWidth excludes the scrollbar, so the bleed can't cause horizontal
// scroll). Until the first measurement it renders at column width.

// The plan-a deployment sits behind Vercel SSO deployment protection, whose
// login interstitial sends X-Frame-Options: DENY and so can't be framed.
// Production therefore frames embed.aifuturesmodel.com: a second domain on
// the SAME Vercel project, listed under Deployment Protection → Exceptions
// (public), where the calculator's middleware serves only /plan-a and its API
// routes (see middleware.ts in the ai-futures-calculator repo, branch
// fixed-training-compute — EMBED_ONLY_HOSTS must match this hostname). In
// dev, frame the locally running calculator (`npm run dev` in the
// ai-futures-calculator-2 worktree .claude/worktrees/pause-reduction-experiment,
// branch fixed-training-compute). The ai-2030 dev server usually claims :3000
// first, so the calculator lands on :3002; if yours runs elsewhere, set
// NEXT_PUBLIC_TAKEOFF_EXPLORER_URL. If the URL points at a port ai-2030
// itself is serving, the iframe frames this site's 404 page — which shows up
// as a cut-off page stuck at the fallback height.
const EXPLORER_URL =
  process.env.NODE_ENV === 'development'
    ? process.env.NEXT_PUBLIC_TAKEOFF_EXPLORER_URL || 'http://localhost:3002/plan-a'
    : 'https://embed.aifuturesmodel.com/plan-a';

// Keep the plate clear of EssayPage's left ToC rail (RAIL_WIDTH=200, shown at
// md+): the frame's left edge starts just right of the rail instead of at the
// viewport edge, so the widget's text column can't collide with rail labels.
const RAIL_CLEARANCE_PX = 224;
const RAIL_VISIBLE_MEDIA_QUERY = '(min-width: 768px)'; // Tailwind md, matching the rail's `hidden md:block`

export default function TakeoffExplorerEmbed() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [bleed, setBleed] = useState<{ pullLeft: number; width: number } | null>(null);

  // Shareable parameter links. The explorer serializes its full parameter
  // state into ITS OWN url (see the write-back effect in the calculator
  // repo's CovertExplorerClient) — invisible inside an iframe. So: (1) on
  // load, this page's own query params are forwarded into the iframe src to
  // seed the explorer; (2) whenever the explorer's state changes it
  // postMessages the state query up, and we rewrite this page's address bar
  // to match via history.replaceState. Copying the supplement's URL then
  // reproduces the current slider settings.
  //
  // `initialQuery` is captured once on mount and is the ONLY thing the iframe
  // src depends on — later state updates must not touch the src or the iframe
  // would reload on every slider change. It starts null (the iframe renders
  // src-less for the first paint) because window.location isn't available
  // during SSR/hydration.
  const [initialQuery, setInitialQuery] = useState<string | null>(null);
  // Params in the initial URL that belong to the supplement page itself (e.g.
  // ?tab= for TabBoxes), carried along when rewriting the URL. Starts as the
  // whole initial query; any key the explorer ever reports as its own state
  // is deleted, so an explorer param from a shared link doesn't stick around
  // after the user resets that slider to its default.
  const preservedParamsRef = useRef(new URLSearchParams());
  // Full height of the explorer widget, reported by the framed page (it
  // observes its root element with a ResizeObserver). The iframe is sized to
  // it so the whole explorer shows with no internal vertical scrollbar; until
  // the first report the iframe falls back to its fixed Tailwind height.
  const [frameHeight, setFrameHeight] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.delete('embed');
    preservedParamsRef.current = params;
    setInitialQuery(params.toString());
  }, []);

  useEffect(() => {
    const explorerOrigin = new URL(EXPLORER_URL).origin;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== explorerOrigin) return;
      const data = event.data as { type?: unknown; query?: unknown; height?: unknown };
      if (!data) return;
      if (data.type === 'plan-a-explorer-height' && typeof data.height === 'number' && data.height > 0) {
        setFrameHeight(data.height);
        return;
      }
      if (data.type !== 'plan-a-explorer-state' || typeof data.query !== 'string') return;
      const stateParams = new URLSearchParams(data.query);
      const preserved = preservedParamsRef.current;
      for (const key of Array.from(stateParams.keys())) preserved.delete(key);
      const merged = new URLSearchParams(preserved);
      stateParams.forEach((value, key) => merged.append(key, value));
      const query = merged.toString();
      const { pathname, hash } = window.location;
      window.history.replaceState(null, '', `${pathname}${query ? `?${query}` : ''}${hash}`);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      // rect.left already includes whatever pull is currently painted on the
      // element, so read the applied inline margin back off the DOM and
      // subtract it to recover the static offset (distance from the viewport's
      // left edge to where the block would sit unpulled). Reading the DOM —
      // not accumulating against React state — keeps this idempotent when the
      // effect re-runs before a set state has rendered (StrictMode's double
      // setup in dev, bursts of resize events).
      const applied = parseFloat(el.style.marginLeft) || 0;
      const staticLeft = el.getBoundingClientRect().left - applied;
      // Frame spans from the rail clearance (or the block's own static left,
      // whichever is smaller) to the viewport's right edge.
      const inset = window.matchMedia(RAIL_VISIBLE_MEDIA_QUERY).matches
        ? Math.min(RAIL_CLEARANCE_PX, staticLeft)
        : 0;
      setBleed({
        pullLeft: staticLeft - inset,
        width: document.documentElement.clientWidth - inset,
      });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  return (
    <div
      ref={wrapRef}
      className="mt-4 mb-2 w-full"
      style={bleed ? { marginLeft: -bleed.pullLeft, width: bleed.width } : undefined}
    >
      <iframe
        src={
          initialQuery === null
            ? undefined
            : `${EXPLORER_URL}?embed=1${initialQuery ? `&${initialQuery}` : ''}`
        }
        title="Plan A takeoff explorer"
        loading="lazy"
        className="block h-[min(620px,62vh)] w-full border-0"
        style={frameHeight !== null ? { height: frameHeight } : undefined}
      />
    </div>
  );
}
